import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  useTheme,
  alpha,
  FormHelperText
} from '@mui/material';
import { logger } from 'utils/logger';
import solutionsService, { Solution } from '../services/SolutionsService';

// Create a component-specific logger
const log = logger.getLogger('AddSolutionDialog');

interface AddSolutionDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: number;
  userId: number;
  userName: string;
  onSolutionAdded: () => void;
}

const AddSolutionDialog: React.FC<AddSolutionDialogProps> = ({
  open,
  onClose,
  accountId,
  userId,
  userName,
  onSolutionAdded
}) => {
  const theme = useTheme();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [selectedSolution, setSelectedSolution] = useState<number | ''>('');
  const [formError, setFormError] = useState<string | null>(null);
  
  // Load solutions list
  const loadAvailableSolutions = useCallback(async () => {
    if (!accountId) return;

    try {
      setLoading(true);
      setError(null);

      // Get all solutions for the account
      const allSolutions = await solutionsService.getAccountSolutions(accountId);

      // Get current user solutions to filter them out
      const userSolutions = await solutionsService.getUserSolutions(userId, accountId);

      // Filter out solutions already assigned to the user
      const userSolutionIds = userSolutions.data.map((s) => s.solution.id);

      const availableSolutions = allSolutions.data.filter(
        (solution) => !userSolutionIds.includes(solution.id)
      );

      setSolutions(availableSolutions);
    } catch (err) {
      log.error('Error loading solutions:', err);
      setError('Failed to load available solutions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [accountId, userId]);

  // Load available solutions when dialog opens
  useEffect(() => {
    if (open) {
      loadAvailableSolutions();
    } else {
      // Reset form when dialog closes
      setSelectedSolution('');
      setFormError(null);
    }
  }, [open, loadAvailableSolutions]);

  // Handle solution selection
  const handleSolutionChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedSolution(event.target.value);
    setFormError(null); // Clear error when user makes a selection
  };
  
  // Handle save
  const handleSave = async () => {
    // Validate form
    if (selectedSolution === '') {
      setFormError('Please select a solution');
      return;
    }
    
    try {
      setSaving(true);
      
      // Add solution to user
      await solutionsService.addSolutionToUser(userId, selectedSolution as number);
      
      // Notify parent component
      onSolutionAdded();
      
    } catch (err) {
      log.error('Error adding solution to user:', err);
      setError('Failed to add solution. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!saving ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 5,
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: theme.palette.background.default,
          py: 2.5,
          px: 3,
          fontWeight: 600
        }}
      >
        Add Solution Access Configuration
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Box
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              borderLeft: `4px solid ${theme.palette.error.main}`,
              p: 2,
              mb: 3,
              borderRadius: 1
            }}
          >
            <Typography color="error.main" variant="body2">
              {error}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
            User: {userName}
          </Typography>
          
          <FormControl 
            fullWidth 
            error={!!formError} 
            disabled={loading || saving}
            sx={{ mt: 1 }}
          >
            <InputLabel id="solution-select-label">Select Solution</InputLabel>
            <Select
              labelId="solution-select-label"
              id="solution-select"
              value={selectedSolution}
              label="Select Solution"
              onChange={handleSolutionChange}
              sx={{ 
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: formError ? 'error.main' : undefined
                }
              }}
            >
              {loading ? (
                <MenuItem disabled value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography>Loading solutions...</Typography>
                  </Box>
                </MenuItem>
              ) : solutions.length === 0 ? (
                <MenuItem disabled value="">No available solutions found</MenuItem>
              ) : (
                solutions.map((solution) => (
                  <MenuItem key={solution.id} value={solution.id}>
                    {solution.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {formError && <FormHelperText>{formError}</FormHelperText>}
          </FormControl>
        </Box>
      </DialogContent>
      
      <DialogActions
        sx={{
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: theme.palette.background.default,
          p: 2.5,
          '& .MuiButton-root': {
            fontWeight: 500,
            px: 2,
            py: 0.7,
            borderRadius: 1.5
          }
        }}
      >
        <Button 
          onClick={onClose} 
          color="inherit" 
          disabled={saving}
          variant="outlined"
          sx={{ 
            '&:hover': { 
              backgroundColor: alpha(theme.palette.divider, 0.1) 
            } 
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={loading || saving || solutions.length === 0 || selectedSolution === ''}
          sx={{ 
            ml: 1,
            position: 'relative',
            minWidth: '80px'
          }}
        >
          {saving ? (
            <>
              <CircularProgress 
                size={24} 
                color="inherit" 
                thickness={4} 
                sx={{ 
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  marginLeft: '-12px',
                  marginTop: '-12px'
                }} 
              />
              <Box sx={{ opacity: 0 }}>Save</Box>
            </>
          ) : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSolutionDialog;